import React, { useContext, useEffect, useState } from 'react';
import { IPaymentTask } from '../../../interfaces/delivery-company/tasks';
import axios, { AxiosResponse } from 'axios';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { API_ENDPOINT } from '../../../configurations/global.config';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { isMobile, isTablet } from 'react-device-detect';
import ConfirmPaymentDialog from './confirm-payment-dialog';
import FilterBar from '../../common/filter-bar/filter-bar';
import DynamicCardListing from '../driver-pickups/driver-pickups-listing/pickup-card-listing';
import { IFilterOption } from '../../common/filter-bar/types';
import PaymentCardListing from './driver-payment-task-listing';
import { useSearchParams } from 'react-router-dom';
import {
  ALL,
  AWAITING_COLLECTION,
  FINISHED,
  IN_PROGRESS,
  PENDING,
  TASK_STATE_CATEGORY,
} from './filter_utils';
import { buildQueryParams } from '../../common/filter-bar/filter-bar.utils';

function DriverPaymentTasks() {
  const [paymentTasks, setPaymentTasks] = useState<IPaymentTask[]>([]);
  const [paymentTasksCount, setPaymentTasksCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [filters, setFilters] = useState<IFilterOption[]>([]);
  const { user, config } = useContext(AuthContext);
  const [refreshKey, setRefreshKey] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const taskStateFilter = searchParams.get('status') || AWAITING_COLLECTION;

  const changeStateCategory = (newTarget: TASK_STATE_CATEGORY) => {
    setSearchParams({ status: newTarget });
  };

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 10,
    searchColumn: '',
    searchValue: '',
    searchOperation: 'equals',
  });

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };
  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
  };

  const taskMutator = (mutator: (task: IPaymentTask) => IPaymentTask) => {
    setPaymentTasks(paymentTasks.map((p) => mutator(p)));
  };

  const handleSuccess = (nextPage: number) => {
    setRefreshKey((prevKey) => prevKey + 1); // Increment the refreshKey to trigger useEffect
    controller.page += nextPage;
  };

  const reloadTasks = async () => {
    setIsLoading(true);
    setMessage('');
    setResponse(undefined);
    let endpoint = `${API_ENDPOINT}/api/v1/payment-tasks/?page_size=200&driver=${user?.id}`;
    endpoint += `&status=${taskStateFilter}`;
    endpoint += buildQueryParams(filters);
    try {
      const response = await axios.get<{
        results: IPaymentTask[];
        count: number;
      }>(endpoint, config);

      setPaymentTasksCount(response.data.count);
      setPaymentTasks(response.data.results);
      setResponse(response);
      console.log(response);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setMessage('Failed to get your payment tasks: ' + e.response?.status);
        setResponse(e.response);
      } else setMessage('Failed to get your payment tasks');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    reloadTasks();
  }, [taskStateFilter, refreshKey, filters]);

  return (
    <>
      <Typography
        variant="h3"
        component="h3"
        sx={{ textAlign: 'center', mt: 0, mb: 0 }}
      >
        Payment Tasks
      </Typography>
      <FilterBar
        filterQueryAction={filterQueryAction}
        currentlySelectedFilters={filters}
        filterKeys={['merchant', 'district']}
        headless
      />
      <br />

      <FormControl component="fieldset" sx={{ marginBottom: 2 }} fullWidth>
        <RadioGroup
          row
          aria-label="state"
          name="state"
          value={taskStateFilter}
          onChange={(e) =>
            changeStateCategory(e.target.value as TASK_STATE_CATEGORY)
          }
          sx={{ display: 'flex', gap: 0, justifyContent: 'center' }}
        >
          <FormControlLabel
            value={AWAITING_COLLECTION}
            control={<Radio size="small" />}
            label="Awaiting Collection"
          />
          <FormControlLabel
            value={PENDING}
            control={<Radio size="small" />}
            label="Pending"
          />
          <FormControlLabel
            value={IN_PROGRESS}
            control={<Radio size="small" />}
            label="In Progress"
          />
          <FormControlLabel
            value={FINISHED}
            control={<Radio size="small" />}
            label="Finished"
          />
          <FormControlLabel
            value={ALL}
            control={<Radio size="small" />}
            label="All"
          />
        </RadioGroup>
      </FormControl>
      <PaymentCardListing
        isLoading={isLoading}
        tasks={paymentTasks}
        statusFilter={taskStateFilter}
        onSuccess={handleSuccess}
        updateTasks={taskMutator}
        totalCount={paymentTasksCount}
        changeStateCategory={changeStateCategory}
        reloadTasks={reloadTasks}
      />
    </>
  );
}

export default DriverPaymentTasks;
